<template>
    <div class="invite">
        <a class="btn btn-primary flex gap-x-xs align-items-center justify-center" :class="extraBtnClasses" v-if="!showingForm" @click="showFormClicked()"><i class="fa-solid fa-paper-plane"></i><span>Invite {{ wrestlerFirstName }}</span></a>
        <div v-if="showingForm" role="form">
            <!-- Blocked: dob unknown, or an under-13 being invited by a non-guardian (COPPA) -->
            <div v-if="blocked">
                <p :class="forAppEmbed ? '' : 'text-gray-700'" class="text-sm">
                    <span v-if="dobMissing">{{ wrestlerFirstName }} needs a date of birth on their profile before a login invite can be sent. Wrestlers under 13 must be invited by their own parent or guardian.</span>
                    <span v-else>Parents must invite their own children if they are under 13.</span>
                </p>
                <div class="button-area text-right invite">
                    <button name="button" @click="hideFormClicked()" class="btn btn-default">Close</button>
                </div>
            </div>
            <div v-else>
                <!-- Guardian of an under-13: email plus consent flow, no child email collected -->
                <div v-if="consentFlow" class="form-group">
                    <p :class="forAppEmbed ? '' : 'text-gray-700'" class="text-sm">
                        Because {{ wrestlerFirstName }} is under 13, we'll email <strong>you</strong> a consent request first.
                        Once you confirm, you'll receive {{ wrestlerFirstName }}'s signup link.
                    </p>
                </div>
                <div v-else class="form-group">
                    <label>{{ wrestlerFirstName }}'s Email</label>
                    <p :class="forAppEmbed ? '' : 'text-gray-700'" class="text-sm ">This should be <strong>{{ wrestlerFirstName }}'s</strong> email address. Parent accounts are created separately.</p>
                    <input v-model="email" type="email" class="form-control">
                    <p v-if="teenFlow" :class="forAppEmbed ? '' : 'text-gray-700'" class="text-sm m-t-xs">
                        Because {{ wrestlerFirstName }} is under 18, any parent or guardian on file will be notified.
                    </p>
                </div>
                <div class="button-area text-right invite">
                    <div v-show="error">
                        <small>
                            <span class="text-danger">{{ errorMessage }}</span>
                        </small>
                    </div>
                    <button name="button" @click="hideFormClicked()" class="btn btn-default">Cancel</button>
                    <ladda-button @lbClicked="postInvite" el-class="btn-primary" :loading="loading">
                        {{ consentFlow ? 'Request Login' : 'Send Invite' }}
                    </ladda-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { appEmbedMix } from './mixins/app_embed_mix';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'invite',
  mixins: [appEmbedMix, errorableMix],
  components: { LaddaButton },
  props: {
    wrestlerId: {
      type: Number,
      required: true,
    },
    wrestlerFirstName: {
      type: String,
      default: '',
    },
    // ISO date string (yyyy-mm-dd). Drives the COPPA / minor consent flows;
    // when absent we fail safe and require a birthdate before inviting.
    wrestlerDob: {
      type: String,
      default: '',
    },
    // Whether the viewer is a guardian of this wrestler (parent-facing mounts).
    // Advisory only — InvitePolicy enforces the real rule server side.
    isGuardian: {
      type: Boolean,
      default: false,
    },
    reloadPage: {
      type: Boolean,
      default: false,
    },
    extraBtnClasses: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,

      email: '',
      showingForm: false,
    };
  },
  computed: {
    dobMissing() {
      return !this.wrestlerDob;
    },
    age() {
      if (this.dobMissing) {
        return null;
      }

      const [year, month, day] = this.wrestlerDob.split('-').map(Number);
      const now = new Date();
      const beforeBirthday = now.getMonth() + 1 < month
        || (now.getMonth() + 1 === month && now.getDate() < day);
      return now.getFullYear() - year - (beforeBirthday ? 1 : 0);
    },
    blocked() {
      return this.dobMissing || (this.age < 13 && !this.isGuardian);
    },
    consentFlow() {
      return this.isGuardian && !this.dobMissing && this.age < 13;
    },
    teenFlow() {
      return !this.dobMissing && this.age >= 13 && this.age < 18;
    },
  },
  methods: {
    hideFormClicked() {
      const vm = this;
      vm.showingForm = false;
      vm.$emit('hide');
    },
    showFormClicked() {
      const vm = this;
      vm.showingForm = true;
      vm.$emit('show');
    },
    validateNotEmpty(email) {
      const vm = this;

      if (email.length === 0) {
        vm.showError('Please enter an email address.');
        return false;
      }
      return true;
    },
    successToast(email) {
      const vm = this;
      if (vm.consentFlow) {
        return `Check ${email} — confirm the consent request to get ${vm.wrestlerFirstName}'s signup link`;
      }
      return `${email} has been sent an invite`;
    },
    postInvite: _.throttle(function () {
      const vm = this;

      if (vm.loading === true) {
        return;
      }

      if (!vm.consentFlow && !vm.validateNotEmpty(vm.email)) {
        return;
      }

      vm.clearError();
      vm.loading = true;
      const params = {
        invite: {
          // The server issues the consent request to the guardian's own email
          // for under-13s, so no address is collected here in that flow.
          email: vm.consentFlow ? '' : vm.email,
          inviteable_id: vm.wrestlerId,
          inviteable_type: 'WrestlerProfile',
        },
      };
      const url = vm.$apiService.invitesUrl();
      axios.post(url, params)
        .then((response) => {
          vm.loading = false;
          vm.showingForm = false;
          const { email } = response.data;
          vm.$parent.$emit('invite-success', email);
          vm.$notificationManager.emit('show-toast', vm.successToast(email), true);
          if (vm.forAppEmbed) {
            vm.askAppToReloadPage();
          } else if (vm.reloadPage) {
            window.location.reload();
          }
        })
        .catch((error) => {
          vm.loading = false;
          const mergedInto = error.response?.status === 410 ? error.response?.data?.merged_into : null;
          if (mergedInto) {
            vm.handleMergedProfile(mergedInto);
            return;
          }
          const baseErrors = error.response?.data?.errors?.base;
          if (Array.isArray(baseErrors) && baseErrors.length > 0) {
            vm.showError(baseErrors[0]);
          } else {
            vm.showError(vm.parseErrorResponse(error));
          }
        });
    }, 500),
    // The wrestler on this page was merged into another profile after the page loaded
    // (server answered 410 with the survivor). Tell the user, then land them on current data:
    // the surviving wrestler's page if we're on the stale wrestler's page, otherwise a reload
    // (roster list, next steps, parent walkthrough, and the native family invites embed all
    // list the survivor already).
    handleMergedProfile(mergedInto) {
      const vm = this;
      vm.$notificationManager.emit('show-toast', `${vm.wrestlerFirstName}'s profile was merged with a duplicate. Refreshing...`, true);
      if (vm.forAppEmbed) {
        vm.askAppToReloadPage();
        return;
      }
      const onStaleWrestlerPage = new RegExp(`^/wrestlers/${vm.wrestlerId}(/|$)`).test(window.location.pathname);
      if (onStaleWrestlerPage && mergedInto.type === 'WrestlerProfile') {
        window.location.assign(`/wrestlers/${mergedInto.id}`);
      } else {
        window.location.reload();
      }
    },
  },
};
</script>
